import React, { useState, useEffect } from 'react';
import { Task, CreateTaskData, UpdateTaskData, PRIORITIES, STATUSES, TaskImage } from '../types/Task';
import { X, Save, Calendar, Clock, User, Tag, Link } from 'lucide-react';
import ComboBox from './ComboBox';
import TagInput from './TagInput';
import ImageUpload from './ImageUpload';
import { columnApi, taskApi } from '../services/api';
import type { KanbanColumn } from '../services/api';
import './TaskModal.css';

interface TaskModalProps {
  task?: Task | null;
  onSave: (data: CreateTaskData | UpdateTaskData) => void;
  onClose: () => void;
  existingTasks?: Task[];
  onTaskImagesChanged?: (taskId: string) => void;
}

// Inline styles to ensure they work
const modalStyles = {
  overlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '1rem',
    backdropFilter: 'blur(4px)',
  },
  content: {
    backgroundColor: 'var(--bg-color)',
    border: '2px solid var(--sub-color)',
    borderRadius: '12px',
    width: '100%',
    maxWidth: '800px',
    maxHeight: '90vh',
    overflowY: 'auto' as const,
    animation: 'modalSlideIn 0.3s ease-out',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.5rem 2rem',
    borderBottom: '2px solid var(--sub-alt-color)',
  },
  title: {
    color: 'var(--text-color)',
    fontSize: '1.5rem',
    fontWeight: 600,
    margin: 0,
  },
  closeBtn: {
    padding: '0.5rem',
    backgroundColor: 'var(--sub-alt-color)',
    color: 'var(--text-color)',
    border: '1px solid var(--sub-color)',
    borderRadius: '6px',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  form: {
    padding: '2rem',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1.5rem',
    marginBottom: '2rem',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.5rem',
  },
  fullWidth: {
    gridColumn: '1 / -1',
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    color: 'var(--text-color)',
    fontWeight: 500,
    fontSize: '0.875rem',
  },
  input: {
    padding: '0.75rem',
    backgroundColor: 'var(--sub-alt-color)',
    border: '2px solid transparent',
    borderRadius: '8px',
    color: 'var(--text-color)',
    fontSize: '0.875rem',
    transition: 'all 0.2s ease',
    fontFamily: 'inherit',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '1rem',
    paddingTop: '1.5rem',
    borderTop: '2px solid var(--sub-alt-color)',
  },
  cancelBtn: {
    padding: '0.75rem 1.5rem',
    border: '1px solid var(--sub-color)',
    borderRadius: '8px',
    fontWeight: 600,
    fontSize: '0.875rem',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    cursor: 'pointer',
    backgroundColor: 'var(--sub-alt-color)',
    color: 'var(--text-color)',
  },
  saveBtn: {
    padding: '0.75rem 1.5rem',
    border: 'none',
    borderRadius: '8px',
    fontWeight: 600,
    fontSize: '0.875rem',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    cursor: 'pointer',
    backgroundColor: 'var(--main-color)',
    color: 'var(--bg-color)',
    boxShadow: '0 2px 8px rgba(226, 183, 20, 0.2)',
  },
};

const TaskModal: React.FC<TaskModalProps> = ({ task, onSave, onClose, existingTasks = [], onTaskImagesChanged }) => {
  const [formData, setFormData] = useState<CreateTaskData>({
    summary: '',
    description: '',
    tag: '',
    priority: 'medium',
    labels: [],
    status: 'todo',
    videoTutorialTitle: '',
    videoTutorialLink: '',
    owner: '',
    reportedBy: '',
    expectedDate: '',
    originalEstimate: 0,
  });

  const [actualTime, setActualTime] = useState(0);
  const [workStartedAt, setWorkStartedAt] = useState('');
  const [workCompletedAt, setWorkCompletedAt] = useState('');
  const [availableStatuses, setAvailableStatuses] = useState<string[]>(['todo']);
  const [availableColumns, setAvailableColumns] = useState<KanbanColumn[]>([]);
  const [taskImages, setTaskImages] = useState<TaskImage[]>([]);

  // Extract unique values from existing tasks
  const getUniqueValues = (field: keyof Task): string[] => {
    const values = existingTasks
      .map(task => task[field])
      .filter((value): value is string => typeof value === 'string' && value.trim() !== '')
      .map(value => value.trim());
    return Array.from(new Set(values)).sort();
  };

  const getUniqueLabels = (): string[] => {
    const allLabels = existingTasks
      .flatMap(task => task.labels || [])
      .filter(label => label.trim() !== '')
      .map(label => label.trim());
    return Array.from(new Set(allLabels)).sort();
  };

  const availableTags = getUniqueValues('tag');
  const availableOwners = getUniqueValues('owner');
  const availableReporters = getUniqueValues('reportedBy');
  const availableLabels = getUniqueLabels();

  // Load columns to get available statuses
  useEffect(() => {
    const loadColumns = async () => {
      try {
        const response = await columnApi.getColumns();
        const columns = response.columns;
        setAvailableColumns(columns);
        const statuses = columns.map((col: KanbanColumn) => col.status);
        setAvailableStatuses(statuses.length > 0 ? statuses : ['todo']);
      } catch (error) {
        console.error('Failed to load columns:', error);
        setAvailableStatuses(['todo']); // fallback
        setAvailableColumns([]);
      }
    };

    loadColumns();
  }, []);

  // Get the display name for status from column title
  const getStatusDisplayName = (status: string): string => {
    const column = availableColumns.find(col => col.status === status);
    return column?.title || status.replace('-', ' ');
  };

  // Initialize form data when task changes
  useEffect(() => {
    if (task) {
      setFormData({
        summary: task.summary,
        description: task.description,
        tag: task.tag,
        priority: task.priority,
        labels: task.labels,
        status: task.status,
        videoTutorialTitle: task.videoTutorialTitle,
        videoTutorialLink: task.videoTutorialLink,
        owner: task.owner,
        reportedBy: task.reportedBy,
        expectedDate: task.expectedDate ? task.expectedDate.split('T')[0] : '',
        originalEstimate: task.originalEstimate,
      });
      setActualTime(task.actualTime);
      setWorkStartedAt(task.workStartedAt ? task.workStartedAt.split('T')[0] : '');
      setWorkCompletedAt(task.workCompletedAt ? task.workCompletedAt.split('T')[0] : '');
      setTaskImages(task.images || []);
    } else {
      // Reset fields for new task
      setWorkStartedAt('');
      setWorkCompletedAt('');
      setTaskImages([]);
    }
  }, [task]);

  // Load task images when editing an existing task
  useEffect(() => {
    const loadTaskImages = async () => {
      if (task?.id) {
        try {
          const response = await taskApi.getTaskImages(task.id);
          console.log('🖼️ Loaded task images:', response);
          setTaskImages(response.images || []);
        } catch (error) {
          console.error('Failed to load task images:', error);
          setTaskImages(task.images || []); // Fallback to task.images
        }
      }
    };

    loadTaskImages();
  }, [task?.id]);

  const handleInputChange = (field: keyof CreateTaskData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prepare expected date - convert to ISO format if provided
    let expectedDateFormatted: string | undefined = undefined;
    if (formData.expectedDate && formData.expectedDate.trim()) {
      try {
        // Convert YYYY-MM-DD to ISO format
        const date = new Date(formData.expectedDate + 'T00:00:00.000Z');
        expectedDateFormatted = date.toISOString();
      } catch (error) {
        console.warn('Invalid date format:', formData.expectedDate);
      }
    }

    // Prepare work dates
    let workStartedAtFormatted: string | undefined = undefined;
    if (workStartedAt && workStartedAt.trim()) {
      try {
        const date = new Date(workStartedAt + 'T00:00:00.000Z');
        workStartedAtFormatted = date.toISOString();
      } catch (error) {
        console.warn('Invalid work started date format:', workStartedAt);
      }
    }

    let workCompletedAtFormatted: string | undefined = undefined;
    if (workCompletedAt && workCompletedAt.trim()) {
      try {
        const date = new Date(workCompletedAt + 'T00:00:00.000Z');
        workCompletedAtFormatted = date.toISOString();
      } catch (error) {
        console.warn('Invalid work completed date format:', workCompletedAt);
      }
    }
    
    const submitData: CreateTaskData | UpdateTaskData = {
      ...formData,
      originalEstimate: Number(formData.originalEstimate) || 0,
      // Don't send empty strings for optional fields, send undefined instead
      expectedDate: expectedDateFormatted,
      description: formData.description || undefined,
      tag: formData.tag || undefined,
      videoTutorialTitle: formData.videoTutorialTitle || undefined,
      videoTutorialLink: formData.videoTutorialLink || undefined,
      owner: formData.owner || undefined,
      reportedBy: formData.reportedBy || undefined,
    };

    if (task) {
      // Include actual time and work dates for updates
      (submitData as UpdateTaskData).actualTime = Number(actualTime) || 0;
      (submitData as UpdateTaskData).workStartedAt = workStartedAtFormatted;
      (submitData as UpdateTaskData).workCompletedAt = workCompletedAtFormatted;
    }

    console.log('📤 Submitting task data:', submitData);
    onSave(submitData);
  };

  // Handle images change
  const handleImagesChange = (images: TaskImage[]) => {
    setTaskImages(images);
    
    // Notify parent component about image changes if we have a task
    if (task && onTaskImagesChanged) {
      onTaskImagesChanged(task.id);
    }
  };

  return (
    <div style={modalStyles.overlay} onClick={onClose}>
      <div style={modalStyles.content} onClick={(e) => e.stopPropagation()}>
        <div style={modalStyles.header}>
          <h2 style={modalStyles.title}>
            {task ? `Edit Task` : 'Create New Task'}
            {task && (
              <span style={{fontSize: '0.875rem', fontWeight: 400, color: 'var(--sub-color)', marginLeft: '0.5rem'}}>
                ID: {task.id}
              </span>
            )}
          </h2>
          <button style={modalStyles.closeBtn} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={modalStyles.form}>
          <div style={modalStyles.grid}>
            {/* Summary */}
            <div style={{...modalStyles.formGroup, ...modalStyles.fullWidth}}>
              <label htmlFor="summary" style={modalStyles.label}>
                <Tag size={16} />
                Summary *
              </label>
              <input
                id="summary"
                type="text"
                style={modalStyles.input}
                value={formData.summary}
                onChange={(e) => handleInputChange('summary', e.target.value)}
                placeholder="Enter task summary..."
                required
              />
            </div>

            {/* Description */}
            <div style={{...modalStyles.formGroup, ...modalStyles.fullWidth}}>
              <label htmlFor="description" style={modalStyles.label}>Description</label>
              <textarea
                id="description"
                style={{...modalStyles.input, minHeight: '80px', resize: 'vertical'}}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Enter task description..."
                rows={3}
              />
            </div>

            {/* Priority and Status */}
            <div style={modalStyles.formGroup}>
              <label htmlFor="priority" style={modalStyles.label}>Priority</label>
              <select
                id="priority"
                style={{...modalStyles.input, cursor: 'pointer'}}
                value={formData.priority}
                onChange={(e) => handleInputChange('priority', e.target.value)}
              >
                {PRIORITIES.map(priority => (
                  <option key={priority} value={priority}>
                    {priority}
                  </option>
                ))}
              </select>
            </div>

            <div style={modalStyles.formGroup}>
              <label htmlFor="status" style={modalStyles.label}>Status</label>
              <select
                id="status"
                style={{...modalStyles.input, cursor: 'pointer'}}
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
              >
                {availableStatuses.map(status => (
                  <option key={status} value={status}>
                    {getStatusDisplayName(status)}
                  </option>
                ))}
              </select>
            </div>

            {/* Tag with ComboBox */}
            <div style={modalStyles.formGroup}>
              <ComboBox
                id="tag"
                label="Tag"
                value={formData.tag || ''}
                onChange={(value) => handleInputChange('tag', value)}
                options={availableTags}
                placeholder="e.g., frontend, backend..."
              />
            </div>

            {/* Owner with ComboBox */}
            <div style={modalStyles.formGroup}>
              <ComboBox
                id="owner"
                label="Owner"
                value={formData.owner || ''}
                onChange={(value) => handleInputChange('owner', value)}
                options={availableOwners}
                placeholder="Assigned to..."
              />
            </div>

            {/* Labels with TagInput */}
            <div style={{...modalStyles.formGroup, ...modalStyles.fullWidth}}>
              <TagInput
                id="labels"
                label="Labels"
                value={formData.labels || []}
                onChange={(labels) => handleInputChange('labels', labels)}
                availableTags={availableLabels}
                placeholder="Add labels..."
              />
            </div>

            {/* Video Tutorial */}
            <div style={modalStyles.formGroup}>
              <label htmlFor="videoTitle" style={modalStyles.label}>
                <Link size={16} />
                Video Tutorial Title
              </label>
              <input
                id="videoTitle"
                type="text"
                style={modalStyles.input}
                value={formData.videoTutorialTitle}
                onChange={(e) => handleInputChange('videoTutorialTitle', e.target.value)}
                placeholder="Tutorial title..."
              />
            </div>

            <div style={modalStyles.formGroup}>
              <label htmlFor="videoLink" style={modalStyles.label}>Video Tutorial Link</label>
              <input
                id="videoLink"
                type="url"
                style={modalStyles.input}
                value={formData.videoTutorialLink}
                onChange={(e) => handleInputChange('videoTutorialLink', e.target.value)}
                placeholder="https://..."
              />
            </div>

            {/* Dates and Time */}
            <div style={modalStyles.formGroup}>
              <label htmlFor="expectedDate" style={modalStyles.label}>
                <Calendar size={16} />
                Expected Date
              </label>
              <input
                id="expectedDate"
                type="date"
                style={{...modalStyles.input, colorScheme: 'dark'}}
                value={formData.expectedDate}
                onChange={(e) => handleInputChange('expectedDate', e.target.value)}
              />
            </div>

            {/* Reported By with ComboBox */}
            <div style={modalStyles.formGroup}>
              <ComboBox
                id="reportedBy"
                label="Reported By"
                value={formData.reportedBy || ''}
                onChange={(value) => handleInputChange('reportedBy', value)}
                options={availableReporters}
                placeholder="Reporter name..."
              />
            </div>

            {/* Time Estimates */}
            <div style={modalStyles.formGroup}>
              <label htmlFor="originalEstimate" style={modalStyles.label}>
                <Clock size={16} />
                Original Estimate (hours)
              </label>
              <input
                id="originalEstimate"
                type="number"
                min="0"
                step="0.5"
                style={modalStyles.input}
                value={formData.originalEstimate}
                onChange={(e) => handleInputChange('originalEstimate', e.target.value)}
                placeholder="0"
              />
            </div>

            {task && (
              <div style={modalStyles.formGroup}>
                <label htmlFor="actualTime" style={modalStyles.label}>Actual Time (hours)</label>
                <input
                  id="actualTime"
                  type="number"
                  min="0"
                  step="0.5"
                  style={modalStyles.input}
                  value={actualTime}
                  onChange={(e) => setActualTime(Number(e.target.value))}
                  placeholder="0"
                />
              </div>
            )}

            {/* Work Started Date */}
            {task && (
              <div style={modalStyles.formGroup}>
                <label htmlFor="workStartedAt" style={modalStyles.label}>
                  <Calendar size={16} />
                  Work Started At
                </label>
                <input
                  id="workStartedAt"
                  type="date"
                  style={{...modalStyles.input, colorScheme: 'dark'}}
                  value={workStartedAt}
                  onChange={(e) => setWorkStartedAt(e.target.value)}
                />
              </div>
            )}

            {/* Work Completed Date */}
            {task && (
              <div style={modalStyles.formGroup}>
                <label htmlFor="workCompletedAt" style={modalStyles.label}>
                  <Calendar size={16} />
                  Work Completed At
                </label>
                <input
                  id="workCompletedAt"
                  type="date"
                  style={{...modalStyles.input, colorScheme: 'dark'}}
                  value={workCompletedAt}
                  onChange={(e) => setWorkCompletedAt(e.target.value)}
                />
              </div>
            )}
          </div>

          {/* Image Upload Section */}
          <div style={{...modalStyles.fullWidth, marginBottom: '1.5rem'}}>
            <ImageUpload
              taskId={task?.id}
              images={taskImages}
              onImagesChange={handleImagesChange}
              disabled={!task} // Only allow uploads for existing tasks
            />
          </div>

          <div style={modalStyles.footer}>
            <button type="button" style={modalStyles.cancelBtn} onClick={onClose}>
              Cancel
            </button>
            <button type="submit" style={modalStyles.saveBtn}>
              <Save size={16} />
              {task ? 'Update Task' : 'Create Task'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TaskModal; 